import { useEffect, useState } from "react";
import { Sparkles, Brain, CheckCircle2, Loader2 } from "lucide-react";

interface QuizGenerationLoaderProps {
  topic: string;
}

const steps = [
  { icon: Brain, text: "Analyzing your topic...", duration: 8000 },
  { icon: Sparkles, text: "Selecting difficulty level...", duration: 6000 },
  { icon: Brain, text: "Generating MCQs...", duration: 20000 },
  { icon: Sparkles, text: "Optimizing answer options...", duration: 10000 },
  { icon: CheckCircle2, text: "Almost ready...", duration: 6000 },
];

const funFacts = [
  "Did you know? Your first instinct in MCQs is often right!",
  "Studies show regular quizzing improves long-term memory retention by 50%.",
  "Taking practice tests is more effective than re-reading material.",
  "Spacing out your quiz practice leads to better results than cramming.",
  "Writing down your reasoning before answering improves accuracy.",
  "MCQs were first introduced in 1914 by Frederick J. Kelly.",
  "Testing yourself is one of the most powerful learning strategies.",
  "The 'testing effect' shows that retrieval practice strengthens memory.",
  "Short, frequent quizzes are more effective than long, infrequent ones.",
  "Explaining your answer choice to yourself improves understanding.",
];

const QuizGenerationLoader = ({ topic }: QuizGenerationLoaderProps) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);

  useEffect(() => {
    // Progress through steps based on their duration
    let stepTimer: NodeJS.Timeout;
    
    const moveToNextStep = () => {
      setCurrentStepIndex((prev) => {
        if (prev < steps.length - 1) {
          const nextIndex = prev + 1;
          stepTimer = setTimeout(moveToNextStep, steps[nextIndex].duration);
          return nextIndex;
        }
        return prev;
      });
    };

    // Start with first step
    if (currentStepIndex < steps.length - 1) {
      stepTimer = setTimeout(moveToNextStep, steps[currentStepIndex].duration);
    }

    return () => clearTimeout(stepTimer);
  }, [currentStepIndex]);

  useEffect(() => {
    // Rotate fun facts every 5 seconds
    const factTimer = setInterval(() => {
      setCurrentFactIndex((prev) => (prev + 1) % funFacts.length);
    }, 5000);

    return () => clearInterval(factTimer);
  }, []);

  const CurrentIcon = steps[currentStepIndex]?.icon || Brain;

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
      <div className="w-full max-w-xl mx-auto">
        <div className="bg-card rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-card border border-border animate-scale-in">
          {/* Animated icon */}
          <div className="flex justify-center mb-4 sm:mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
              <div className="relative bg-gradient-primary rounded-full p-4 sm:p-5 md:p-6">
                <CurrentIcon className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-primary-foreground animate-pulse" />
              </div>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-1 sm:mb-2 bg-gradient-primary bg-clip-text text-transparent">
            Generating Your Quiz
          </h2>
          <p className="text-center text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6 md:mb-8 capitalize">
            {topic}
          </p>

          {/* Step indicators */}
          <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6 md:mb-8">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = index === currentStepIndex;
              const isCompleted = index < currentStepIndex;

              return (
                <div
                  key={index}
                  className={`flex items-center space-x-2 sm:space-x-3 transition-all duration-500 ${
                    isActive ? "scale-105" : "scale-100 opacity-50"
                  }`}
                >
                  <div
                    className={`rounded-full p-2 sm:p-2.5 transition-all duration-500 flex-shrink-0 ${
                      isCompleted
                        ? "bg-success text-success-foreground"
                        : isActive
                        ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <StepIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </div>
                  <span
                    className={`text-sm sm:text-base font-medium transition-colors duration-500 flex-1 ${
                      isActive ? "text-foreground" : "text-muted-foreground"
                    }`}
                  >
                    {step.text}
                  </span>
                  {isActive && (
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin text-primary flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Fun Facts Section */}
          <div className="pt-4 sm:pt-6 border-t border-border">
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 min-h-[80px] sm:min-h-[100px] flex items-center justify-center">
              <div className="text-center space-y-1 sm:space-y-2">
                <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-primary animate-pulse" />
                  <span className="text-xs sm:text-sm font-semibold text-primary">Did You Know?</span>
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-primary animate-pulse" />
                </div>
                <p
                  key={currentFactIndex}
                  className="text-xs sm:text-sm text-foreground/80 leading-relaxed animate-fade-in px-2"
                >
                  {funFacts[currentFactIndex]}
                </p>
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4 sm:mt-6">
            <div className="h-1.5 sm:h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-primary transition-all duration-1000 ease-out rounded-full"
                style={{ 
                  width: `${((currentStepIndex + 1) / steps.length) * 100}%` 
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizGenerationLoader;
